import * as THREE from 'three/webgpu';

/**
 * @file Loads the PLAYER's art: `public/sprites/people/player.png`
 * (260x406 pixel-art chibi human, faces RIGHT, transparent background,
 * alpha-bbox cropped with 2 px of padding so the feet sit on the bottom edge).
 *
 * It is one of the 26 humans in `public/sprites/people/` — the rest are the
 * cast (`src/game/enemies/*.enemy.json`) and go through
 * `actors/enemyArt.js#loadSpriteArt`, which shares this file's
 * {@link configureSpriteTexture}. The old monster art
 * (`public/sprites/enemy_sprites/`) and the old player (`chibi_combat.png`)
 * were deleted with the framework demo content, owner decision 2026-08-04.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE FILTER COMBINATION IS THE WHOLE POINT
 * ─────────────────────────────────────────────────────────────────────────────
 * `magFilter: Nearest` — the sprite is ~1.3 world units tall on a 52-degree
 *   camera; without it the pixel art turns to mush the moment you zoom in.
 * `minFilter: LinearMipmapLinear` + `generateMipmaps` — Nearest MINIFICATION on
 *   a moving sprite crawls and sparkles horribly (the classic "pixel art shimmer"
 *   that makes people blame the engine). Mipmaps cost 33% memory on one 256px
 *   texture: free, in practice.
 * `colorSpace: SRGB` — it is authored colour, not data.
 *
 * There is deliberately NO sprite-sheet slicing: the walk cycle is a transform
 * (see `actors/WalkWobble.js`), per the user's decision.
 *
 * @module engine/assets/playerSprite
 */

/**
 * @typedef {Object} PlayerSprite
 * @property {THREE.Texture} texture Ready to hand to a `SpriteNodeMaterial`.
 * @property {number} aspect `width / height` of the source image
 *   ({@link PLAYER_ASPECT} for the shipped art). Multiply by the sprite's world
 *   height to get its width.
 * @property {number} width Source pixels.
 * @property {number} height Source pixels.
 * @property {() => void} dispose
 */

/** Where the art lives once `public/` is served. */
export const PLAYER_SPRITE_URL = '/sprites/people/player.png';

/**
 * World height of the player billboard, metres. CHIBI proportions: the art is
 * ~3.5 heads tall, so 1.3 m reads as an adult standing beside the 1.32 m gang
 * members and the city's 3.5 m pavement-to-storey rhythm. It is only a
 * DEFAULT — `Player` takes `height`, and a caller may override it.
 */
export const PLAYER_HEIGHT = 1.3;

/**
 * `width / height` of `player.png` (260 x 406). Used as the fallback aspect
 * before the texture decodes; once it does, the real value wins. Feet stay on
 * the ground regardless — `Player` pivots on `center = (0.5, 0)` and the art is
 * bbox-cropped, so a wrong aspect would only make the chibi fat, never float.
 */
export const PLAYER_ASPECT = 0.64;

/** @type {Map<string, Promise<PlayerSprite>>} */
const _cache = new Map();

/**
 * @param {string} [url]
 * @param {Object} [o]
 * @param {number} [o.anisotropy=1] Raise to 4 if the sprite ever tilts far from
 *   camera-facing (it does not today — sprites are screen-aligned).
 * @param {boolean} [o.cache=true] Share one texture between callers. Turn OFF
 *   when the caller intends to `dispose()` it (the editor preview does).
 * @returns {Promise<PlayerSprite>}
 */
export function loadPlayerSprite(url = PLAYER_SPRITE_URL, o = {}) {
  const { anisotropy = 1, cache = true } = o;
  const key = `${url}|${anisotropy}`;
  if (cache && _cache.has(key)) return /** @type {Promise<PlayerSprite>} */ (_cache.get(key));

  const p = new THREE.TextureLoader()
    .loadAsync(url)
    .then((texture) => {
      configureSpriteTexture(texture, anisotropy);
      const width = texture.image?.width ?? 0;
      const height = texture.image?.height ?? 0;
      return {
        texture,
        aspect: height > 0 ? width / height : 1,
        width,
        height,
        dispose() {
          texture.dispose();
          if (cache) _cache.delete(key);
        },
      };
    })
    .catch((err) => {
      if (cache) _cache.delete(key);
      throw new Error(`loadPlayerSprite: could not load ${url} (${err?.message ?? err})`);
    });

  if (cache) _cache.set(key, p);
  return p;
}

/**
 * Applies the project's pixel-art sprite filtering to any texture. Exported so
 * procedurally generated sprite atlases (dev scenes, `ImpostorBaker`) match the
 * player exactly instead of each inventing their own settings.
 *
 * @param {THREE.Texture} texture Mutated in place.
 * @param {number} [anisotropy=1]
 * @returns {THREE.Texture} The same texture, for chaining.
 */
export function configureSpriteTexture(texture, anisotropy = 1) {
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.generateMipmaps = true;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = anisotropy;
  texture.needsUpdate = true;
  return texture;
}
